import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import {
  fetchStructuralRev,
  postStructuralRev,
  fetchAllRevsForProject,
  updateStructuralRev,
  getProjectById,
} from "./api";

import { useParams } from "react-router-dom";
import { useGeneralContext } from "../../../AllEquipments/CommonFeatures/SidePanel/GeneralContext";

import {
  buildCombinationRows,
  mergeWithPrevious,
} from "./StructureModelingTabs/PartLibTab/buildCombinationRows";

import { secondAxios } from "../../../../../api/axios";
import { notifyProjectSaved } from "../../../../../components/Drawers/TreeViewDrawer/treeView/utils/projectSavedEvent";
import { useAuthContext } from "../../../../../context/authContext";
import useWbsUpdated from "../../../../../hooks/useWbsUpdated";
import {
  buildRevisionSaveStamp,
  stripRevisionSaveStamp,
} from "../../../../../utils/revisionSaveStamp";
import {
  isEmptyRevisionEntry,
  revisionDocumentUID,
  stripRevisionDocumentUID,
} from "../../../../../utils/revisionUID";

const STRUCTURAL_TYPE = "structural";

const getRowsFromSqliteResponse = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data?.rows)) return response.data.rows;
  return [];
};

/**
 * @typedef {Object} StructureModelingContextValue
 * @property {*} partLibTable
 * @property {Function} setPartLibTable
 * @property {*} specTable
 * @property {Function} setSpecTable
 * @property {*} estimationTable
 * @property {Function} setEstimationTable
 * @property {Object} allTables
 * @property {Function} initializeAllTables
 * @property {{label: string, spec: string, rev: string | number}[]} selectedSpecs
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => void} addSlectedSpec
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => void} removeSelectedSpec
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => Object} getSelectedSpecTable
 * @property {Function} getComponentCodeTable
 * @property {Object} modelingData
 * @property {Function} setModelingData
 * @property {Function} saveStructuralData
 * @property {Function} setProjectPath
 * @property {string | null} projectPath
 */

/** @type {React.Context<StructureModelingContextValue | undefined>} */
const StructureModelingContext = createContext(undefined);

/**
 * StructureModelingProvider - Context provider for structure modeling state.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export const StructureModelingProvider = ({ children }) => {
  const [allTables, setAllTables] = useState({});
  const [specTable, setSpecTable] = useState([]);
  const [fullSpecTable, setFullSpecTable] = useState([]);
   const[selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [partLibTable, setPartLibTable] = useState({});
  const [estimationTable, setEstimationTable] = useState({ 0: {} });
  // Id of the project whose data is currently loaded (null while a load is
  // in flight) - see loadStructuralData.
  const [loadedProjectId, setLoadedProjectId] = useState(null);
  const [modelingData, setModelingData] = useState({});
  const [revision, setRevision] = useState(undefined);
  const [projectPath, setProjectPath] = useState(null);
  const [selectedModelingRevision, setSelectedModelingRevision] = useState("0");
  // Starts empty on purpose: no revision is selected until the user picks one
  const [selectedEstimationRevision, setSelectedEstimationRevision] =
    useState("");

  // A revision is only “confirmed” once the user picks it from the Rev.
  // dropdown in the header. Until then the Estimation tab has no revision to
  // read, so loading a project shows its revision list without showing any of
  // its data — same flow as Pressure Vessel.
  const [confirmedEstimationRevision, setConfirmedEstimationRevision] =
    useState(null);
  const [clientName, setClientName] = useState("");

  // The two halves of a revision's Rev UID - the plant/project this WBS
  // sits under and the WBS's own name, i.e. the "PROJECT# UID" and
  // "WBS UID" of the header (see saveStructuralData).
  const [plantName, setPlantName] = useState("");
  const [wbsName, setWbsName] = useState("");
  // Renaming a WBS in the projects tree changes half of every Rev UID
  // saved from here on, so that re-runs the fetch that reads these.
  const [wbsRefreshTick, setWbsRefreshTick] = useState(0);
  useWbsUpdated(selectedProjectId, () =>
    setWbsRefreshTick((tick) => tick + 1),
  );

  // Whose name gets stamped onto the revision being saved (see
  // saveStructuralData).
  const { user } = useAuthContext() || {};

  const { fastenersTable, setFastenersTable } = useGeneralContext();

  const [bomData, setBomData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [estimationTableWithTotalSummary, setEstimationTableWithTotalSummary] =
    useState({});

  const [totalSummary, setTotalSummary] = useState([
    { name: "Total Materials Cost", value: "", unit: "$", rate: "", total: "" },
    { name: "Total Man Hours", value: "", unit: "hr", rate: "", total: "" },
    { name: "Galvanized Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Painting Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Fire Proof Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Miscellaneous", value: "", unit: "LS", rate: "1", total: "" },
  ]);

  const [allCombinationTable, setAllCombinationTable] = useState([]);

  /*
   * Checked rows grouped by tab, feeding the Update / Update Assembly buttons.
   *
   * Two sources: rows checked in the Spec Library (unchanged), and Estimation
   * rows the user completed via the cascade dropdowns. The latter contribute
   * `__specRow` — the allCombinationTable row they resolved to — because
   * downstream consumers expect combination-row shape, not Estimation-row shape.
   *
   * Only the active estimation revision contributes: a row completed under an
   * older revision must not feed an update the user is running against the one
   * they are viewing.
   */
  const SelectedParts = useMemo(() => {
    const byTab = {};
    const seen = new Set();

    /*
     * Deduplicated: the same spec can be reached from both the Spec Library and
     * a completed Estimation row, and two Estimation rows can resolve to the
     * same spec. __specRow is a copy, so identity comparison would not catch
     * either case.
     */
    const add = (row) => {
      if (!row?.select || !row?.TabName) return;

      const key = [
        row.TabName,
        row.Spec,
        row.Components,
        row.Size,
        row.Material,
      ].join("|");
      if (seen.has(key)) return;
      seen.add(key);

      if (!byTab[row.TabName]) byTab[row.TabName] = [];
      byTab[row.TabName].push(row);
    };

    (allCombinationTable || []).forEach(add);

    const activeRevision = estimationTable?.[selectedEstimationRevision];
    (activeRevision?.Estimation || []).forEach((row) => add(row?.__specRow));

    return byTab;
  }, [allCombinationTable, estimationTable, selectedEstimationRevision]);

  /*
   * Mark the combination row a completed Estimation row resolved to as selected.
   *
   * The Estimation cascade dropdowns carry only a copy of the combination row
   * (`__specRow`); flagging the copy is not enough for SelectedParts's
   * allCombinationTable pass to see it, so the row inside this array must be
   * flagged directly.
   */
  const markCombinationRowSelected = useCallback((specRow) => {
    if (!specRow) return;

    setAllCombinationTable((prev) =>
      (prev || []).map((row) =>
        row?.Spec === specRow?.Spec &&
        row?.Components === specRow?.Components &&
        row?.Size === specRow?.Size &&
        row?.Material === specRow?.Material &&
        row?.TabName === specRow?.TabName
          ? { ...row, select: true }
          : row,
      ),
    );
  }, []);

  function processSpecData(rows) {
    const seen = new Map();

    for (const row of rows) {
      const key = `${row.Spec}|${row.Components}|${row.Size}|${row.Material}|${row.Requirements}|${row.Rev}`;

      if (!seen.has(key)) {
        seen.set(key, row);
      }
    }

    const uniqueRows = Array.from(seen.values());

    return uniqueRows.sort((a, b) => {
      const compCompare = (a.Components || "").localeCompare(
        b.Components || "",
      );

      if (compCompare !== 0) return compCompare;

      const sizeA = parseFloat(a.Size) || 0;
      const sizeB = parseFloat(b.Size) || 0;

      if (sizeA !== sizeB) return sizeA - sizeB;

      return (a.Size || "").localeCompare(b.Size || "");
    });
  }

  const initializeAllTables = useCallback(async () => {
    try {
      const tableNamesResponse = await secondAxios.post(`/sqlite-db/query`, {
        dbName: "structure",
        sql: `
          SELECT name
          FROM sqlite_master
          WHERE type = 'table'
          ORDER BY name
        `,
      });

      const tableNameRows = getRowsFromSqliteResponse(tableNamesResponse);

      const tableNames = tableNameRows
        .map((row) => row?.name)
        .filter(Boolean);

      const tables = {};

      for (const tableName of tableNames) {
        const safeTableName = String(tableName).replace(/"/g, '""');

        const tableDataResponse = await secondAxios.post(`/sqlite-db/query`, {
          dbName: "structure",
          sql: `SELECT * FROM "${safeTableName}"`,
        });

        tables[tableName] = getRowsFromSqliteResponse(tableDataResponse);
      }

      setAllTables(tables);

      const allSpecData = [];

      const specTableKeys = Object.keys(tables || {}).filter((key) =>
        key.startsWith("structural_spec_"),
      );

      for (const specKey of specTableKeys) {
        const specData = Array.isArray(tables[specKey])
          ? tables[specKey]
          : [];

        allSpecData.push(...specData);
      }

      setFullSpecTable([...allSpecData]);

      const uniqueSpecData = processSpecData(allSpecData);
      setSpecTable(uniqueSpecData);
    } catch (error) {
      console.error("Failed to initialize structural tables:", error);

      setAllTables({});
      setFullSpecTable([]);
      setSpecTable([]);
    }
  }, []);

  function addSlectedSpec(newSelc) {
    setSelectedSpecs((prev) => {
      const exists = prev.some((item) => item.label === newSelc.label);
      return exists ? prev : [...prev, newSelc];
    });
  }

  function removeSelectedSpec(toRemove) {
    setSelectedSpecs((prev) =>
      prev.filter((item) => item.label !== toRemove.label),
    );
  }

  const getSelectedSpecTable = useCallback(
    (selc) => {
      if (!selc) return [];
      if (!specTable || !Array.isArray(specTable)) return [];

      return specTable.filter(
        (item) =>
          item.Spec === selc.spec &&
          item.Rev?.toString() === selc.rev?.toString(),
      );
    },
    [specTable],
  );

  function getComponentCodeTable() {
    return allTables["mc_components"];
  }

  const addNewEstimationRevision = useCallback(() => {
    const revisions = Object.keys(estimationTable)
      .filter((key) => !isNaN(Number(key)))
      .map(Number)
      .sort((a, b) => a - b);

    const nextRev = String(
      revisions.length > 0 ? Math.max(...revisions) + 1 : 1,
    );

    const latestRev =
      revisions.length > 0 ? String(revisions[revisions.length - 1]) : "0";

    const latestData = estimationTable[latestRev] || {};

    setEstimationTable((prev) => ({
      ...prev,
      // Without stripping them the new revision would open under the UID of
      // the revision it branched from, claiming it was already saved by
      // whoever last saved that one. Both are written back on save.
      [nextRev]: stripRevisionDocumentUID(
        stripRevisionSaveStamp(JSON.parse(JSON.stringify(latestData))),
      ),
    }));

    setSelectedEstimationRevision(nextRev);

    return nextRev;
  }, [estimationTable]);

  // `saveStamp` is who is saving and when, `documentUID` the revision's name
  // in the projects tree - saveStructuralData builds both and hands them in
  // so they land on the revision's own entry, next to totalSummary.
  function buildStructuralPayload(saveStamp, documentUID = "") {
    console.log("estimationTable total summary", totalSummary);

    const rev = selectedEstimationRevision;
    const currentRevData = estimationTable[rev] || {};

    // With no revision selected there is nothing to stamp the summary onto —
    // writing it anyway would add a junk "" key to the saved JSON.
    const updatedEstimationTable = rev
      ? {
          ...estimationTable,
          [rev]: {
            ...currentRevData,
            totalSummary: totalSummary || [],
            ...(documentUID ? { documentUID } : {}),
            ...(saveStamp || {}),
          },
        }
      : { ...estimationTable };

    setEstimationTableWithTotalSummary(updatedEstimationTable);

    if (currentRevData && currentRevData.BOM) {
      const bomData1 = currentRevData.BOM.map((item) => ({
        PnPID: null,
        "Cost Code": "Structure",
        Description: item["Material Description"],
        Qty: item.Qty,
        Unit: item.Unit,
        "Weight (lb)": item.Weight,
        "Cost (US $)": item.MaterialCost,
      }));

      setBomData(bomData1);
    }

    if (currentRevData && currentRevData.Summary) {
      const summaryData1 = currentRevData.Summary.filter(
        (cur) => !cur.isSummary,
      ).map((item) => ({
        PnPID: null,
        "Cost Code": "Structure",
        Components: item.Components,
        "Material Description": item["Material Description"],
        Qty: item.Qty,
        "Total Weight (lb)": item.Weight,
        "Total Material Cost (US $)": item.MaterialCost,
        "Total Manhour": +(
          (item.WeldHours || 0) +
          (item.FireProofingCost || 0) +
          (item.GalvanizingCost || 0) +
          (item.PaintingCost || 0)
        ).toFixed(2),
        Remarks: null,
      }));

      setSummaryData(summaryData1);
    }

    const payload = {
      name: selectedProjectId,
      type: STRUCTURAL_TYPE,
      revision: revision?.id,
      data: {
        selectedSpecs,
        partLibTable,
        SelectedParts,
        estimationTable: updatedEstimationTable,
        modelingData,
        projectPath,
        Est_BOM_Summary_DGV: bomData || [],
        DGV_Estimation_Est: summaryData || [],
      },
    };

    return payload;
  }

  async function saveStructuralData() {
    // Who saved this revision, and when. Kept inside the revision's own entry
    // in estimationTable so the header's Rev. field can report it per
    // revision, not just for the last save made in this session.
    const saveStamp = buildRevisionSaveStamp(user);
    const stampedRevision = String(selectedEstimationRevision ?? "").trim();

    // The revision's name in the projects tree, built the same way PV builds
    // its Input Data one (see utils/revisionUID.js). Skipped while the
    // plant/WBS names are still loading rather than written as a
    // "PLANT-PROJECT-..." placeholder - whatever is already stored stands.
    const documentUID =
      plantName && wbsName
        ? revisionDocumentUID(plantName, wbsName, stampedRevision)
        : "";

    const data = buildStructuralPayload(saveStamp, documentUID);

    console.log("saving", data);

    if (revision?.id) {
      await updateStructuralRev(selectedProjectId, revision.id, data);
    } else {
      const rev = await postStructuralRev(selectedProjectId, {
        name: "0",
        data: data?.data || {},
      });

      console.log("Created new revision:", rev);
      setRevision(rev);
    }

    // Only once the save went through - a failed save leaves the header
    // showing the stamp that is actually still stored.
    if (stampedRevision) {
      setEstimationTable((prev) => ({
        ...prev,
        [stampedRevision]: {
          ...(prev?.[stampedRevision] || {}),
          ...(documentUID ? { documentUID } : {}),
          ...saveStamp,
        },
      }));
    }

    notifyProjectSaved(selectedProjectId);
  }

  useEffect(() => {
    window.__menuHandlers = window.__menuHandlers || {};

    window.__menuHandlers.SAVE = saveStructuralData;
    window.__menuHandlers.ADD_ESTIMATION_REV = addNewEstimationRevision;

    return () => {
      if (window.__menuHandlers?.SAVE === saveStructuralData) {
        window.__menuHandlers.SAVE = null;
      }

      if (
        window.__menuHandlers?.ADD_ESTIMATION_REV === addNewEstimationRevision
      ) {
        window.__menuHandlers.ADD_ESTIMATION_REV = null;
      }
    };
  }, [
    revision,
    selectedModelingRevision,
    selectedEstimationRevision,
    selectedSpecs,
    partLibTable,
    SelectedParts,
    modelingData,
    projectPath,
    estimationTableWithTotalSummary,
    summaryData,
    bomData,
    estimationTable,
    totalSummary,
    addNewEstimationRevision,
  ]);

  function parseStructuralData(raw) {
    const data = raw?.data || {};

    console.log("Parsed structural data:", data);

    setProjectPath(data.projectPath || null);
    setSelectedSpecs(data.selectedSpecs || []);

    setPartLibTable(() => {
      const saved = data.partLibTable;

      if (!saved || Object.keys(saved).length === 0) return {};

      const keys = Object.keys(saved);

      const looksLikeLegacyRevisioned = keys.every(
        (k) =>
          /^\d+$/.test(k) &&
          saved[k] &&
          typeof saved[k] === "object" &&
          !Array.isArray(saved[k]),
      );

      if (looksLikeLegacyRevisioned) {
        const lastRev = keys[keys.length - 1];
        return saved[lastRev] || {};
      }

      return saved;
    });

    let parsedEstimationTable = {};

    if (data.estimationTable && Object.keys(data.estimationTable).length > 0) {
      parsedEstimationTable = data.estimationTable;
    } else {
      parsedEstimationTable = { 0: {} };
    }

    setEstimationTable(parsedEstimationTable);
    setEstimationTableWithTotalSummary(parsedEstimationTable);

    const revisions = Object.keys(parsedEstimationTable)
      .filter((key) => !isNaN(Number(key)))
      .sort((a, b) => Number(a) - Number(b));

    // Deliberately NOT auto-selecting the latest revision. The dropdown gets
    // its options from estimationTable, but nothing is shown until the user
    // confirms a revision, so switching projects never surfaces stale data.
    setSelectedEstimationRevision("");
    setConfirmedEstimationRevision(null);

    // Per-revision BOM / Summary / totalSummary are populated by
    // selectEstimationRevision once the user confirms a revision. Only the
    // legacy project-level tables are safe to restore up front.
    if (data.Est_BOM_Summary_DGV && data.DGV_Estimation_Est) {
      setBomData(data.Est_BOM_Summary_DGV);
      setSummaryData(data.DGV_Estimation_Est);
    } else {
      setBomData([]);
      setSummaryData([]);
    }

    setModelingData(() => {
      if (!data.modelingData || Object.keys(data.modelingData).length === 0) {
        return {
          0: {
            templatePath: null,
            categories: [],
          },
        };
      }

      return data.modelingData;
    });
  }


  // ─── Estimation revisions ───────────────────────────────────────────────────
  // Options come straight from the revision keys inside the loaded JSON.
  const estimationRevisions = useMemo(
    () =>
      Object.keys(estimationTable || {})
        .filter((key) => key !== "" && !isNaN(Number(key)))
        .sort((a, b) => Number(a) - Number(b)),
    [estimationTable],
  );

  // Confirming a revision is what actually surfaces its data — the tables read
  // selectedEstimationRevision, and the per-revision summaries are restored
  // here rather than at load time.
  const selectEstimationRevision = useCallback(
    (revisionKey) => {
      const key = String(revisionKey ?? "");

      setSelectedEstimationRevision(key);
      setConfirmedEstimationRevision(
        key ? { projectId: selectedProjectId, revision: key } : null,
      );

      if (!key) return;

      const revisionData = estimationTable?.[key] || {};

      if (revisionData.totalSummary) setTotalSummary(revisionData.totalSummary);
      if (revisionData.BOM) setBomData(revisionData.BOM);
      if (revisionData.Summary) setSummaryData(revisionData.Summary);
    },
    [estimationTable, selectedProjectId],
  );

  // Branch the current revision into a fresh one and switch to it.
  const addEstimationRevision = useCallback(() => {
    // A project with no saved estimation loads with a blank { 0: {} }
    // placeholder (see parseStructuralData), as does one whose Rev 0 the
    // tree seeded with nothing but its Rev UID. That empty Rev 0 is the first
    // revision still to be filled in, so open it rather than branching Rev 1.
    if (
      estimationRevisions.length === 1 &&
      estimationRevisions[0] === "0" &&
      isEmptyRevisionEntry(estimationTable?.["0"])
    ) {
      setSelectedEstimationRevision("0");
      setConfirmedEstimationRevision({
        projectId: selectedProjectId,
        revision: "0",
      });
      return "0";
    }

    const numbers = estimationRevisions.map(Number).filter((n) => !isNaN(n));
    const nextRev = String(numbers.length ? Math.max(...numbers) + 1 : 0);

    // A new revision starts as a copy of the latest one in the WBS - not of
    // whichever revision happens to be selected, since opening from the tree
    // selects none and would otherwise start the new revision empty.
    setEstimationTable((prev) => {
      const latestRev = Object.keys(prev || {})
        .filter((key) => key !== "" && !isNaN(Number(key)))
        .sort((a, b) => Number(b) - Number(a))[0];
      return {
        ...prev,
        // Same reasoning as addNewEstimationRevision: a branched revision has
        // not been saved by anyone yet, and has no Rev UID of its own until
        // it is.
        [nextRev]: stripRevisionDocumentUID(
          stripRevisionSaveStamp(
            JSON.parse(JSON.stringify(prev?.[latestRev] || {})),
          ),
        ),
      };
    });

    setSelectedEstimationRevision(nextRev);
    setConfirmedEstimationRevision({
      projectId: selectedProjectId,
      revision: nextRev,
    });

    return nextRev;
  }, [estimationRevisions, estimationTable, selectedProjectId]);

  const deleteEstimationRevision = useCallback(
    (revToDelete) => {
      const remaining = estimationRevisions.filter((r) => r !== revToDelete);

      setEstimationTable((prev) => {
        const updated = { ...(prev || {}) };
        delete updated[revToDelete];
        return updated;
      });

      if (selectedEstimationRevision === revToDelete) {
        setSelectedEstimationRevision("");
        setConfirmedEstimationRevision(null);
      }

      return remaining;
    },
    [estimationRevisions, selectedEstimationRevision],
  );

  async function loadStructuralData(projectIdValue) {
    setLoadedProjectId(null);
    const revisions = await fetchAllRevsForProject(projectIdValue);
    let rev;

    if (!revisions.length) {
      rev = await postStructuralRev(projectIdValue, {
        name: "0",
        data: "{}",
      });
    } else {
      rev = revisions[revisions.length - 1];
    }

    console.log("Loaded revision:", rev);

    setRevision(rev);

    const jsonData = await fetchStructuralRev(projectIdValue, rev.id);
    parseStructuralData(jsonData);
    // Marks the point where estimationTable/estimationRevisions describe THIS
    // project - deep links from the EMS tree wait on it before selecting (or
    // branching) a revision, since parseStructuralData resets the selection.
    setLoadedProjectId(String(projectIdValue));
  }

  useEffect(() => {
    if (selectedProjectId) {
      loadStructuralData(selectedProjectId);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) return;

    async function fetchProject() {
      try {
        const base = await getProjectById(selectedProjectId);
        const nextClientName = String(base?.plant?.clientName || "DEMO").trim();

        setClientName(nextClientName);
        // Same call, so the Rev UID costs no extra request.
        setPlantName(String(base?.plant?.name || "").trim());
        setWbsName(String(base?.name || "").trim());
      } catch (error) {
        console.error("Failed to fetch project clientName:", error);
        setClientName("DEMO");
        setPlantName("");
        setWbsName("");
      }
    }

    fetchProject();
  }, [selectedProjectId, wbsRefreshTick]);

  useEffect(() => {
    if (!selectedProjectId) return;
    if (!clientName) return;

    initializeAllTables(clientName);
  }, [selectedProjectId, clientName, initializeAllTables]);

  // ─── Rebuild allCombinationTable whenever the spec selection changes ─────────
  //
  // This runs in the context rather than in the Spec Library tab, so adding or
  // removing a spec regenerates the rows no matter which tab is open. Estimation
  // reads these rows for its dropdown options and therefore never needs the Spec
  // Library tab to have been visited.
  const selectedSpecRows = useMemo(() => {
    if (!Array.isArray(selectedSpecs) || !selectedSpecs.length) return [];
    return selectedSpecs.flatMap(getSelectedSpecTable);
  }, [selectedSpecs, getSelectedSpecTable]);

  const prevSpecsKeyRef = useRef(null);

  useEffect(() => {
    if (!Object.keys(allTables || {}).length) return;

    const specsKey = [
      ...new Set(selectedSpecRows.map((r) => r?.Spec).filter(Boolean)),
    ]
      .sort()
      .join(",");

    if (prevSpecsKeyRef.current === specsKey) return;

    if (!selectedSpecRows.length) {
      // Don't wipe a populated table while specTable/allTables are still loading
      if (allCombinationTable.length > 0) return;
      prevSpecsKeyRef.current = specsKey;
      setAllCombinationTable([]);
      return;
    }

    prevSpecsKeyRef.current = specsKey;

    const rows = buildCombinationRows(selectedSpecRows, allTables);
    if (!rows.length) return;

    setAllCombinationTable((prev) =>
      mergeWithPrevious(rows, prev, partLibTable),
    );
  }, [selectedSpecRows, allTables, partLibTable, allCombinationTable.length]);

  return (
    <StructureModelingContext.Provider
      value={{
        selectedProjectId,
        setSelectedProjectId,

        partLibTable,
        setPartLibTable,

        specTable,
        setSpecTable,

        fullSpecTable,
        setFullSpecTable,

        estimationTable,
        setEstimationTable,

        allTables,
        initializeAllTables,

        selectedSpecs,
        setSelectedSpecs,
        addSlectedSpec,
        removeSelectedSpec,
        getSelectedSpecTable,

        getComponentCodeTable,

        modelingData,
        setModelingData,

        saveStructuralData,

        projectPath,
        setProjectPath,

        totalSummary,
        setTotalSummary,

        selectedModelingRevision,
        setSelectedModelingRevision,

        fastenersTable,
        setFastenersTable,

        bomData,
        setBomData,

        summaryData,
        setSummaryData,

        estimationTableWithTotalSummary,
        setEstimationTableWithTotalSummary,

        selectedEstimationRevision,
        setSelectedEstimationRevision,
        confirmedEstimationRevision,
        estimationRevisions,
        selectEstimationRevision,
        addEstimationRevision,
        deleteEstimationRevision,

        allCombinationTable,
        setAllCombinationTable,
        markCombinationRowSelected,

        loadStructuralData,
        loadedProjectId,

        SelectedParts,

        clientName,
        setClientName,

        addNewEstimationRevision,
      }}
    >
      {children}
    </StructureModelingContext.Provider>
  );
};

export const useStructureModeling = () => {
  const context = useContext(StructureModelingContext);

  if (!context) {
    throw new Error(
      "useStructureModeling must be used within a StructureModelingProvider",
    );
  }

  return context;
};